import React from 'react';
import { SchematicShell, Line } from './SchematicPrimitives';

const IN = 0.0254;

/* ── desk plant schematic (succulent in cylindrical pot) ───────────── */

const DESK_POT_FRAC = 0.44; // pot is ~44% of total height
const DESK_RIM_H = 0.3 * IN;

export function DormDeskPlantSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const isSide = view === 'left' || view === 'right';
  const w = isSide ? depth : width;

  const potH = height * DESK_POT_FRAC;
  const plantH = height - potH;
  const potBottom = height; // SVG y-axis: bottom of pot = height
  const potTop = height - potH;
  const potRimTop = potTop;
  const potRimBottom = potTop + DESK_RIM_H;

  // Pot taper: bottom is narrower
  const taperInset = w * 0.1;

  // Foliage: rounded mound silhouette
  const foliageCx = w / 2;
  const foliageRx = w * 0.55;
  const foliageRy = plantH * 0.5;
  const foliageCy = potRimTop - plantH * 0.45;

  return (
    <SchematicShell
      width={w}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      {/* Pot outline — trapezoid (wider at top, narrower at bottom) */}
      <path
        d={[
          `M ${0} ${potRimBottom}`,
          `L ${taperInset} ${potBottom}`,
          `L ${w - taperInset} ${potBottom}`,
          `L ${w} ${potRimBottom}`,
          'Z',
        ].join(' ')}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />

      {/* Pot rim — slightly wider rectangle at top of pot */}
      <rect
        x={-w * 0.02}
        y={potRimTop}
        width={w * 1.04}
        height={DESK_RIM_H}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />

      {/* Soil line inside pot */}
      <Line
        x1={w * 0.12}
        y1={potRimBottom + 0.15 * IN}
        x2={w * 0.88}
        y2={potRimBottom + 0.15 * IN}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth * 0.5}
      />

      {/* Foliage — elliptical mound silhouette */}
      <ellipse
        cx={foliageCx}
        cy={foliageCy}
        rx={foliageRx}
        ry={foliageRy}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />

      {/* Inner detail — smaller rosette shapes to suggest succulent */}
      <ellipse
        cx={foliageCx - foliageRx * 0.3}
        cy={foliageCy + foliageRy * 0.15}
        rx={foliageRx * 0.35}
        ry={foliageRy * 0.4}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth * 0.5}
        vectorEffect="non-scaling-stroke"
      />
      <ellipse
        cx={foliageCx + foliageRx * 0.3}
        cy={foliageCy + foliageRy * 0.1}
        rx={foliageRx * 0.35}
        ry={foliageRy * 0.38}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth * 0.5}
        vectorEffect="non-scaling-stroke"
      />
      <ellipse
        cx={foliageCx}
        cy={foliageCy - foliageRy * 0.25}
        rx={foliageRx * 0.3}
        ry={foliageRy * 0.35}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth * 0.5}
        vectorEffect="non-scaling-stroke"
      />
    </SchematicShell>
  );
}

DormDeskPlantSchematic.supportsSideViews = true;

/* ── floor plant schematic (snake plant in tall planter) ───────────── */

const FLOOR_POT_FRAC = 0.4; // planter is ~40% of total height
const FLOOR_RIM_H = 0.4 * IN;

export function DormFloorPlantSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const isSide = view === 'left' || view === 'right';
  const w = isSide ? depth : width;

  const potH = height * FLOOR_POT_FRAC;
  const plantH = height - potH;
  const potBottom = height;
  const potTop = height - potH;
  const potRimBottom = potTop + FLOOR_RIM_H;

  // Pot taper
  const taperInset = w * 0.12;

  // Snake plant leaves — tall vertical blades fanning out
  const numLeaves = 5;
  const leafWidth = w * 0.08;
  const centerX = w / 2;
  const spreadW = w * 0.35;

  const leaves = [];
  for (let i = 0; i < numLeaves; i++) {
    const frac = i / (numLeaves - 1); // 0 to 1
    const lx = centerX - spreadW + frac * spreadW * 2;

    // Vary leaf height for natural look — center leaves tallest
    const distFromCenter = Math.abs(frac - 0.5) * 2;
    const leafH = plantH * (0.95 - distFromCenter * 0.35);

    const leafTop = potTop - leafH;
    const leafBottom = potRimBottom - 0.1 * IN;

    // Slight lean outward for outer leaves
    const lean = (frac - 0.5) * w * 0.12;
    const tipX = lx + lean;

    // Draw leaf as a narrow pointed shape
    leaves.push(
      <path
        key={`leaf-${i}`}
        d={[
          `M ${lx - leafWidth / 2} ${leafBottom}`,
          `L ${tipX} ${leafTop}`,
          `L ${lx + leafWidth / 2} ${leafBottom}`,
        ].join(' ')}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    );
  }

  return (
    <SchematicShell
      width={w}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      {/* Planter outline — trapezoid */}
      <path
        d={[
          `M ${0} ${potRimBottom}`,
          `L ${taperInset} ${potBottom}`,
          `L ${w - taperInset} ${potBottom}`,
          `L ${w} ${potRimBottom}`,
          'Z',
        ].join(' ')}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />

      {/* Planter rim */}
      <rect
        x={-w * 0.03}
        y={potTop}
        width={w * 1.06}
        height={FLOOR_RIM_H}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />

      {/* Soil line */}
      <Line
        x1={w * 0.15}
        y1={potRimBottom + 0.2 * IN}
        x2={w * 0.85}
        y2={potRimBottom + 0.2 * IN}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth * 0.5}
      />

      {/* Snake plant leaves */}
      {leaves}
    </SchematicShell>
  );
}

DormFloorPlantSchematic.supportsSideViews = true;
